import type { Discount as GQLItem } from '../../../generated/api.graphql';
import { take } from '../../../util';
import { DiscountKind } from './DiscountKind';
import { DiscountType } from './DiscountType';

/**
 * A discount code for an event — the row the Discounts tab lists and its form saves.
 *
 * **`numericAmount` and `kind` are the source of truth; `amount` is display only.** The string is
 * the backend's canonical rendering (`"$10.0"` / `"10.0%"`), kept so a row can print it, but
 * nothing may parse it: that parse — `amount =~ /%/` — is the guess the pair replaced. The wire
 * type's `isFixedAmount` / `isPercentage` are the same guess in boolean form, so they are neither
 * selected nor mapped.
 */
export type Discount = {
  id: number;
  eventId: number;
  label: string;
  code?: string;
  amount: string;
  numericAmount: number;
  kind: DiscountKind;
  type: DiscountType;
  limit?: number;
  timesUsed: number;
  usable: boolean;
  expiration?: string;
  joinitMembershipType?: string;
  ticketBlueprintIds: number[];
  createdAt?: string;
  updatedAt?: string;
};

/**
 * `take` for the non-nullable fields, `?? undefined` for all six nullable ones: `take` skips a key
 * only when its value is `undefined`, and the backend answers an absent nullable field with a
 * `null` that would land in a field typed `T | undefined`. As `App/Event/EventTicketType.ts`.
 */
export const fromGql = (item: GQLItem): Discount => ({
  ...take<Discount>(item, 'id', 'eventId', 'label', 'amount', 'numericAmount', 'kind', 'type',
    'timesUsed', 'usable', 'ticketBlueprintIds'),
  code: item.code ?? undefined,
  limit: item.limit ?? undefined,
  expiration: item.expiration ?? undefined,
  joinitMembershipType: item.joinitMembershipType ?? undefined,
  createdAt: item.createdAt ?? undefined,
  updatedAt: item.updatedAt ?? undefined,
});
